import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ScannerSection, closeCameraAfterScanFlash, SCAN_SUCCESS_FLASH_MS, type ScannerSectionHandle } from '../scanner';
import OrderDetailCameraBar, { CameraIcon } from '../orders/orderdetail/OrderDetailCameraBar';
import { extractStorageIdFromQr } from '../orders/orderdetail/scanningLogic';
import { QR_UNREADABLE_STORAGE_ID_MESSAGE } from '../scanner/qrScanMessages';
import { getLabelByStorageIdPrefix, getLabelStatus } from '../../services/shelfIndexAPI';
import { formatCreatedAt } from '../../utils/formatCreatedAt';
import { getPrintOrder } from '../../services/printOrdersAPI';
import {
  PRINT_EVENT_LABEL_NOT_IN_JOB,
  aggregatePrintJobIds,
  findPrintJobId,
  flattenPrintOrderItems,
} from '@/shared/utils/printJobIds';
import {
  PRINT_EVENT_ORDER_QUERY,
  PRINT_EVENT_RETURN_QUERY,
  SCAN_LABEL_PREFIX_QUERY,
  checkLabelHandoffPath,
  parsePrintEventReturnParam,
  parseStorageIdPrefixParam,
  printEventHandlingReturnPath,
  reprintHandoffPath,
  searchWarehouseHandoffPath,
  shelfIndexerHandoffPath,
} from '../../utils/scanLabelHandoff';
import {
  MESSAGE_TONES,
  cameraMessageClass,
  statusSlotMessageClass,
  useStatusMessage,
} from '../../hooks/useStatusMessage';
import LabelDetailsPanel from '../shared/LabelDetailsPanel';
import ProductInfoModal from '../shared/ProductInfoModal';
import ShelfContentsModal from '../shared/ShelfContentsModal';
import '../orders/orderdetail/pick-list.css';
import '../orders/OrdersPilot.css';
import '../warehouse/SearchWarehouse.css';
import './PrintBoxLabel.css';
import './ScanLabel.css';

interface ScannedLabelDetails {
  readonly prefix: string;
  readonly sku: string;
  readonly shelfIndex: string | null;
  readonly type: string | null;
  readonly checkedIn: boolean;
  readonly createdAtDisplay: string;
}

function ScanLabel(): React.ReactElement {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const scannerCaptureRef = useRef<ScannerSectionHandle>(null);
  const loadedPrefixRef = useRef<string | null>(null);
  const urlPrefix = parseStorageIdPrefixParam(searchParams.get(SCAN_LABEL_PREFIX_QUERY));
  const printEventOrderId = searchParams.get(PRINT_EVENT_ORDER_QUERY);
  const printEventReturnPath = parsePrintEventReturnParam(searchParams.get(PRINT_EVENT_RETURN_QUERY));
  const printEventHandoff = Boolean(printEventOrderId && printEventReturnPath);
  const [webcamOn, setWebcamOn] = useState(() => !urlPrefix || printEventHandoff);
  const [details, setDetails] = useState<ScannedLabelDetails | null>(null);
  const [productInfoSku, setProductInfoSku] = useState<string | null>(null);
  const [shelfContentsShelf, setShelfContentsShelf] = useState<string | null>(null);
  const {
    message,
    tone: messageTone,
    invalidInput: errorInvalidInput,
    flashKey: messageKey,
    fades: messageFades,
    showMessage,
    clearMessage,
  } = useStatusMessage();

  const loadLabel = useCallback(
    async (prefix: string): Promise<boolean> => {
      loadedPrefixRef.current = prefix;
      showMessage('Looking up label...');
      const [label, status] = await Promise.all([
        getLabelByStorageIdPrefix(prefix),
        getLabelStatus(prefix),
      ]);
      const sku = label?.sku?.trim();
      if (!label || !sku) {
        setDetails(null);
        showMessage(`Label not found in DB for storage ID ${prefix}`, MESSAGE_TONES.ERROR, {
          invalidInput: prefix,
        });
        return false;
      }
      setDetails({
        prefix,
        sku,
        shelfIndex: label.shelfIndex ?? null,
        type: label.type ?? null,
        checkedIn: status.found ? status.checkedIn : true,
        createdAtDisplay:
          status.found && status.createdAt ? formatCreatedAt(status.createdAt) : '',
      });
      clearMessage();
      return true;
    },
    [showMessage, clearMessage],
  );

  useEffect(() => {
    if (searchParams.get(PRINT_EVENT_ORDER_QUERY)) return;
    const prefix = parseStorageIdPrefixParam(searchParams.get(SCAN_LABEL_PREFIX_QUERY));
    if (!prefix) {
      loadedPrefixRef.current = null;
      setDetails(null);
      return;
    }
    if (loadedPrefixRef.current === prefix) return;
    void loadLabel(prefix);
  }, [searchParams, loadLabel]);

  const handleQrCapture = useCallback(
    async (qrData: string | null): Promise<boolean> => {
      const prefix = extractStorageIdFromQr(qrData);
      if (!prefix) {
        const scanned = qrData?.trim();
        showMessage(QR_UNREADABLE_STORAGE_ID_MESSAGE, MESSAGE_TONES.ERROR, {
          invalidInput: scanned || null,
          fade: true,
        });
        return false;
      }

      const orderId = searchParams.get(PRINT_EVENT_ORDER_QUERY);
      const returnPath = parsePrintEventReturnParam(searchParams.get(PRINT_EVENT_RETURN_QUERY));
      if (orderId && returnPath) {
        const job = await getPrintOrder(orderId);
        if (!job) {
          showMessage('Could not load that print job.', MESSAGE_TONES.ERROR, { fade: true });
          return false;
        }
        const rows = aggregatePrintJobIds(flattenPrintOrderItems(job.sheets));
        if (!findPrintJobId(rows, prefix)) {
          showMessage(PRINT_EVENT_LABEL_NOT_IN_JOB, MESSAGE_TONES.ERROR, {
            invalidInput: prefix,
            fade: true,
          });
          return false;
        }
        window.setTimeout(() => {
          navigate(printEventHandlingReturnPath(returnPath, orderId, prefix));
        }, SCAN_SUCCESS_FLASH_MS);
        return true;
      }

      const found = await loadLabel(prefix);
      if (found) {
        closeCameraAfterScanFlash(setWebcamOn);
        setSearchParams({ [SCAN_LABEL_PREFIX_QUERY]: prefix }, { replace: true });
        return true;
      }
      return false;
    },
    [loadLabel, navigate, searchParams, setSearchParams, showMessage],
  );

  const messageBody = errorInvalidInput && message.includes(errorInvalidInput) ? (
    <>
      {message.split(errorInvalidInput)[0]}
      <span className="pbl-message-invalid">{errorInvalidInput}</span>
      {message.split(errorInvalidInput).slice(1).join(errorInvalidInput)}
    </>
  ) : (
    message
  );

  const cameraMessageOverlay = message ? (
    <div key={messageKey} className={cameraMessageClass(messageTone, messageFades)} role="status">
      {messageBody}
    </div>
  ) : null;

  return (
    <div className={`pbl-container sl-page${webcamOn ? ' sl-camera-open' : ''}`}>
      {printEventHandoff && printEventOrderId && printEventReturnPath ? (
        <button
          type="button"
          className="sl-print-event-close"
          aria-label="Close"
          onClick={() => {
            navigate(printEventHandlingReturnPath(printEventReturnPath, printEventOrderId));
          }}
        >
          ×
        </button>
      ) : null}
      <div className="pbl-page-header-status" role="status" aria-live="polite">
        {message && !webcamOn ? (
          <div key={messageKey} className={statusSlotMessageClass(messageTone)}>
            {messageBody}
          </div>
        ) : null}
      </div>

      {details ? (
        <div className="sl-details">
          <LabelDetailsPanel
            storageIdPrefix={details.prefix}
            createdAtDisplay={details.createdAtDisplay}
            checkedIn={details.checkedIn}
            sku={details.sku}
            shelfIndex={details.shelfIndex}
            type={details.type}
            onSkuClick={setProductInfoSku}
            onShelfClick={setShelfContentsShelf}
          />
          <div className="sl-actions">
            <button
              type="button"
              className="home-button"
              onClick={() => navigate(shelfIndexerHandoffPath(details.prefix, !details.checkedIn))}
            >
              Re-index Label
            </button>
            <button
              type="button"
              className="home-button"
              onClick={() => navigate(reprintHandoffPath(details.prefix))}
            >
              Reprint Label
            </button>
            <button
              type="button"
              className="home-button"
              onClick={() => navigate(searchWarehouseHandoffPath(details.prefix))}
            >
              Search Warehouse
            </button>
            <button
              type="button"
              className="home-button"
              onClick={() => navigate(checkLabelHandoffPath(details.prefix))}
            >
              {details.checkedIn ? 'Check Label Out' : 'Check Label In'}
            </button>
          </div>
        </div>
      ) : (
        <div className="sl-prompt">
          {printEventHandoff
            ? 'Scan the last label that came out of the printer'
            : 'Scan a label'}
        </div>
      )}

      {productInfoSku ? (
        <ProductInfoModal item={{ sku: productInfoSku }} onClose={() => setProductInfoSku(null)} />
      ) : null}
      {shelfContentsShelf ? (
        <ShelfContentsModal shelf={shelfContentsShelf} onClose={() => setShelfContentsShelf(null)} />
      ) : null}

      <OrderDetailCameraBar
        open={webcamOn}
        onShutter={() => scannerCaptureRef.current?.capture()}
        centerActions={
          <button
            type="button"
            className="order-detail-bar-camera-btn order-detail-bar-camera-btn-center"
            onClick={() => setWebcamOn(true)}
            aria-label="Open camera"
          >
            <CameraIcon size={28} />
          </button>
        }
      >
        <ScannerSection
          ref={scannerCaptureRef}
          mode="qr"
          onCapture={handleQrCapture}
          webcamOn={webcamOn}
          setWebcamOn={setWebcamOn}
          className="webcam-capture-container pbl-shelf-indexer-scanner"
          overlay={cameraMessageOverlay}
          cornerAction={
            <button
              type="button"
              className="webcam-viewport-close-btn"
              onClick={(event) => {
                event.stopPropagation();
                setWebcamOn(false);
              }}
              aria-label="Close camera"
            >
              ×
            </button>
          }
        />
      </OrderDetailCameraBar>
    </div>
  );
}

export default ScanLabel;
